/**
 * Icon.tsx — Componente centralizado de ícones SVG
 * Substitui ~200 linhas de SVG inline nas páginas
 * TODOS os paths SVG extraídos das páginas existentes (não inventado)
 */
import { h, JSX } from 'preact';

export interface IconProps {
  name: string;
  size?: number;        // default 16
  color?: string;       // default 'currentColor'
  className?: string;
  style?: JSX.CSSProperties;
  spinning?: boolean;   // para RefreshIcon
}

// Mapa de ícones SVG — paths extraídos das páginas AgentesPage, ChatHubPage, IntegrationsHome
const ICON_MAP: Record<string, { viewBox: string; paths: string[]; fill?: boolean }> = {
  // ── De AgentesPage.tsx (linhas 154-208) ──
  plus: {
    viewBox: '0 0 24 24',
    paths: [
      '<line x1="12" y1="5" x2="12" y2="19"/>',
      '<line x1="5" y1="12" x2="19" y2="12"/>',
    ],
  },
  server: {
    viewBox: '0 0 24 24',
    paths: [
      '<rect x="2" y="2" width="20" height="8" rx="2"/>',
      '<rect x="2" y="14" width="20" height="8" rx="2"/>',
      '<line x1="6" y1="6" x2="6.01" y2="6"/>',
      '<line x1="6" y1="18" x2="6.01" y2="18"/>',
    ],
  },
  chat: {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>',
    ],
  },
  settings: {
    viewBox: '0 0 24 24',
    paths: [
      '<circle cx="12" cy="12" r="3"/>',
      '<path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>',
    ],
  },
  refresh: {
    viewBox: '0 0 24 24',
    paths: [
      '<polyline points="23 4 23 10 17 10"/>',
      '<path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>',
    ],
  },
  terminal: {
    viewBox: '0 0 24 24',
    paths: [
      '<polyline points="4 17 10 11 4 5"/>',
      '<line x1="12" y1="19" x2="20" y2="19"/>',
    ],
  },
  trash: {
    viewBox: '0 0 24 24',
    paths: [
      '<polyline points="3 6 5 6 21 6"/>',
      '<path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>',
    ],
  },
  'external-link': {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>',
      '<polyline points="15 3 21 3 21 9"/>',
      '<line x1="10" y1="14" x2="21" y2="3"/>',
    ],
  },
  more: {
    viewBox: '0 0 24 24',
    paths: [
      '<circle cx="12" cy="12" r="1"/>',
      '<circle cx="19" cy="12" r="1"/>',
      '<circle cx="5" cy="12" r="1"/>',
    ],
  },
  cloud: {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>',
    ],
  },
  bot: {
    viewBox: '0 0 24 24',
    paths: [
      '<rect x="3" y="11" width="18" height="10" rx="2"/>',
      '<circle cx="12" cy="5" r="2"/>',
      '<path d="M12 7v4"/>',
      '<circle cx="8" cy="16" r="1"/>',
      '<circle cx="16" cy="16" r="1"/>',
    ],
  },

  // ── De ChatHubPage.tsx (linhas 1162, 1168, 1176-1187, 1373) ──
  microphone: {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>',
      '<path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"/>',
    ],
  },
  'arrow-up': {
    viewBox: '0 0 24 24',
    paths: [
      '<line x1="12" y1="19" x2="12" y2="5"/>',
      '<polyline points="5 12 12 5 19 12"/>',
    ],
  },
  'user-plus': {
    viewBox: '0 0 24 24',
    paths: [
      '<circle cx="12" cy="8" r="5"/>',
      '<path d="M20 21a8 8 0 00-16 0"/>',
      '<line x1="12" y1="4" x2="12" y2="12"/>',
      '<line x1="8" y1="8" x2="16" y2="8"/>',
    ],
  },
  image: {
    viewBox: '0 0 24 24',
    paths: [
      '<rect x="3" y="3" width="18" height="18" rx="2"/>',
      '<circle cx="8.5" cy="8.5" r="1.5"/>',
      '<polyline points="21 15 16 10 5 21"/>',
    ],
  },
  edit: {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M12 20h9"/>',
      '<path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>',
    ],
  },
  globe: {
    viewBox: '0 0 24 24',
    paths: [
      '<circle cx="12" cy="12" r="10"/>',
      '<line x1="2" y1="12" x2="22" y2="12"/>',
      '<path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>',
    ],
  },
  'chevron-down': {
    viewBox: '0 0 24 24',
    paths: [
      '<polyline points="6 9 12 15 18 9"/>',
    ],
  },

  // ── De IntegrationsHome.tsx (linhas 21-27) — categoryIcons ──
  'message-circle': {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>',
    ],
  },
  'git-branch': {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M6 3v12M18 9a3 3 0 01-3 3h-3M6 15a3 3 0 003 3h3a3 3 0 003-3V9"/>',
    ],
  },
  layout: {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zM3 9h18M9 21V9"/>',
    ],
  },
  'credit-card': {
    viewBox: '0 0 24 24',
    paths: [
      '<path d="M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2zM1 10h22"/>',
    ],
  },
};

// Aliases (ícones duplicados com nomes diferentes)
ICON_MAP['message'] = ICON_MAP['message-circle'];
ICON_MAP['user'] = ICON_MAP['user-plus'];
ICON_MAP['search'] = ICON_MAP['globe'];

export function Icon({
  name,
  size = 16,
  color = 'currentColor',
  className,
  style,
  spinning = false,
}: IconProps) {
  const icon = ICON_MAP[name];
  if (!icon) {
    // Fallback: quadrado cinza para ícones não mapeados (evita quebrar a UI)
    console.warn(`[Icon] Ícone "${name}" não encontrado no mapa`);
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        style={{
          ...style,
          animation: spinning ? 'spin 1s linear infinite' : undefined,
        }}
      >
        <rect x="6" y="6" width="12" height="12" />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={icon.viewBox}
      fill={icon.fill ? color : 'none'}
      stroke={icon.fill ? 'none' : color}
      strokeWidth={icon.fill ? '0' : '2'}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{
        ...style,
        animation: spinning ? 'spin 1s linear infinite' : undefined,
      }}
      dangerouslySetInnerHTML={{ __html: icon.paths.join('') }}
    />
  );
}
